/**
 * API Client Utilities
 * Provides a centralized fetch wrapper with proper credentials handling
 */

import { AUTH_REQUIRED } from '@/types/auth';

export const SESSION_ENDED_MESSAGE = 'Your session has ended. Please sign in again.';

/**
 * Error thrown for non-OK responses; keeps the status, error code and parsed body
 */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string, readonly body?: unknown) {
    super(message);
  }
}

let authRequiredHandler: (() => void) | null = null;

/**
 * Register the callback run when any API call answers 401 + AUTH_REQUIRED.
 * Returns the previous handler so callers can restore it.
 */
export function setAuthRequiredHandler(handler: (() => void) | null): (() => void) | null {
  const previous = authRequiredHandler;
  authRequiredHandler = handler;
  return previous;
}

/**
 * Make an API call with automatic credential inclusion and error handling
 *
 * @param endpoint - API endpoint (e.g., '/api/auth/login')
 * @param options - Fetch options
 * @returns Parsed JSON response
 * @throws ApiError with message from API or generic error; a 401 + AUTH_REQUIRED
 *   runs the registered handler and throws SESSION_ENDED_MESSAGE
 */
export async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    credentials: 'include',  // Important for httpOnly cookies
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}`
    }));
    if (response.status === 401 && error.code === AUTH_REQUIRED) {
      authRequiredHandler?.();
      throw new ApiError(SESSION_ENDED_MESSAGE, response.status, error.code, error);
    }
    throw new ApiError(error.error || error.message || `Request failed with status ${response.status}`, response.status, error.code, error);
  }

  return response.json();
}
